import React, { useEffect, useState } from "react";
import { Save, Settings } from "lucide-react";
import * as ownerApi from "../../api/payroll/payrollSettingsApi";
import * as portalApi from "../../api/payroll/payrollSettingsPortalApi";
import { Button, Card, CardHeader, CardTitle, CardContent, Select, Checkbox, Alert, Skeleton } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const PERIOD_OPTIONS = [
  { label: "Monthly", value: "MONTHLY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Bi-weekly", value: "BI_WEEKLY" },
];

export default function PayrollSettingsPage({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const [form, setForm] = useState({
    enableFixedSalaryByRole: false,
    enableFlexibleSalaryComponents: true,
    autoAssignRoleSalary: true,
    defaultPayPeriodType: "MONTHLY",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getPayrollSettings();
        const data = res?.data || res;
        setForm({
          enableFixedSalaryByRole: !!data.enableFixedSalaryByRole,
          enableFlexibleSalaryComponents: data.enableFlexibleSalaryComponents !== false,
          autoAssignRoleSalary: data.autoAssignRoleSalary !== false,
          defaultPayPeriodType: data.defaultPayPeriodType || "MONTHLY",
        });
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [isPortal, api]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      await api.updatePayrollSettings(form);
      setMessage("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-xl space-y-6 p-4 sm:p-6">
        <Skeleton height={28} width={200} />
        <Card>
          <CardContent className="space-y-4 p-6">
            <Skeleton height={20} width="80%" />
            <Skeleton height={20} width="80%" />
            <Skeleton height={20} width="80%" />
            <Skeleton height={40} width="100%" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4 sm:p-6">
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          <Settings className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Payroll Settings
        </h1>
        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
          Choose which salary modes are active for this institution
        </p>
      </div>

      
      {message && <Alert variant="success">{message}</Alert>}

      <form onSubmit={save}>
        <Card>
          <CardHeader className="bg-neutral-50/50">
            <CardTitle>Salary Modes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            <Checkbox
              label="Enable Fixed Salary by Role (Mode 1)"
              checked={form.enableFixedSalaryByRole}
              onChange={(e) => setForm({ ...form, enableFixedSalaryByRole: e.target.checked })}
            />
            <Checkbox
              label="Enable Flexible Salary Components (Mode 2)"
              checked={form.enableFlexibleSalaryComponents}
              onChange={(e) => setForm({ ...form, enableFlexibleSalaryComponents: e.target.checked })}
            />
            <Checkbox
              label="Auto-assign role salary when staff has no structure"
              checked={form.autoAssignRoleSalary}
              onChange={(e) => setForm({ ...form, autoAssignRoleSalary: e.target.checked })}
            />

            <Select
              label="Default pay period type"
              options={PERIOD_OPTIONS}
              value={form.defaultPayPeriodType}
              onChange={(val) => setForm({ ...form, defaultPayPeriodType: val })}
            />

            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              At least one salary mode must remain enabled.
            </p>

            <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
              <Button
                type="submit"
                variant="primary"
                isLoading={saving}
                leftIcon={<Save className="h-4 w-4" />}
              >
                Save settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}